import {
    type ReactNode,
    useMemo,
    type FC
} from "react";
import {
    ScrollView,
    View
} from "react-native";
import type IBreadcrumbListProps from "./type";
import type {
    BreadcrumbRenderItemConstantType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    getBreadcrumbEntries
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    BreadcrumbItemContext,
    useBreadcrumbContext,
    BreadcrumbContext
} from "../../context";
import type {
    BreadcrumbItemContextType,
    BreadcrumbHiddenItem
} from "../../type";
import {
    getBreadcrumbIsCollapsed
} from "../../stylesheet";
import {
    BreadcrumbBackIcon
} from "../../icons";
import BreadcrumbEllipsis from "../ellipsis";
import BreadcrumbItem from "../item";
import BreadcrumbLink from "../link";

const BreadcrumbItemProvider: FC<BreadcrumbItemContextType & {
    children: ReactNode;
}> = ({
    hiddenItems,
    children,
    isFirst,
    isLast
}) => {
    const value = useMemo(() => ({
        hiddenItems,
        isFirst,
        isLast
    }), [
        hiddenItems,
        isFirst,
        isLast
    ]);

    return <BreadcrumbItemContext.Provider
        value={value}
    >
        {children}
    </BreadcrumbItemContext.Provider>;
};

const BreadcrumbList: FC<IBreadcrumbListProps> = ({
    customTheme: customThemeProp,
    children,
    style,
    ...props
}) => {
    const context = useBreadcrumbContext();

    const {
        itemsBeforeCollapse,
        itemsAfterCollapse,
        collapseBehaviour,
        compactBehaviour,
        isOverflow,
        isCompact,
        maxItems
    } = context;

    const customTheme = customThemeProp ?? context.customTheme;

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const {
        container: containerDynamicStyle
    } = useStyles({
        spaces
    });

    const measureContext = useMemo(() => ({
        ...context,
        isMeasure: true
    }), [
        context
    ]);

    const entries = getBreadcrumbEntries({
        children
    });

    const entryCount = entries.length;

    const isBack = isCompact && compactBehaviour === "back";

    const isCollapsed = !isBack && getBreadcrumbIsCollapsed({
        itemsBeforeCollapse,
        itemsAfterCollapse,
        collapseBehaviour,
        compactBehaviour,
        entryCount,
        isOverflow,
        isCompact,
        maxItems
    });

    const isMeasureEnabled = !context.isMeasure && !isBack && collapseBehaviour === "auto";

    const hiddenItems: Array<BreadcrumbHiddenItem> = entries.slice(itemsBeforeCollapse, entryCount - itemsAfterCollapse).map((entry) => ({
        isDisabled: entry.isDisabled,
        onPress: entry.onPress,
        title: entry.title,
        href: entry.href,
        key: entry.key
    }));

    const onMeasureContentSizeChange = (width: number) => {
        context.handleFullWidthChange(width);
    };

    const renderItem = ({
        hiddenItems: currentHiddenItems = [],
        isFirst,
        isLast,
        node,
        key
    }: BreadcrumbRenderItemConstantType) => {
        return <BreadcrumbItemProvider
            hiddenItems={currentHiddenItems}
            isFirst={isFirst}
            isLast={isLast}
            key={key}
        >
            {node}
        </BreadcrumbItemProvider>;
    };

    const renderEntries = (isFull: boolean) => {
        const nodes: Array<ReactNode> = [];

        entries.forEach((entry, index) => {
            const isHidden = !isFull && isCollapsed && index >= itemsBeforeCollapse && index < entryCount - itemsAfterCollapse;

            if(isHidden) {
                if(index === itemsBeforeCollapse) {
                    nodes.push(renderItem({
                        isFirst: nodes.length === 0,
                        key: "breadcrumb-ellipsis",
                        node: <BreadcrumbItem>
                            <BreadcrumbEllipsis />
                        </BreadcrumbItem>,
                        isLast: false,
                        hiddenItems
                    }));
                }

                return;
            }

            nodes.push(renderItem({
                isLast: index === entryCount - 1,
                isFirst: nodes.length === 0,
                node: entry.element,
                key: entry.key
            }));
        });

        return nodes;
    };

    if(isBack) {
        const parent = entries[entryCount - 2];

        if(!parent) {
            return null;
        }

        return <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            role="list"
        >
            {renderItem({
                node: <BreadcrumbItem>
                    <BreadcrumbLink
                        aria-label={localize("breadcrumb-parent-page", [
                            parent.title
                        ])}
                        isDisabled={parent.isDisabled}
                        icon={BreadcrumbBackIcon}
                        onPress={parent.onPress}
                        title={parent.title}
                        href={parent.href}
                    />
                </BreadcrumbItem>,
                key: "breadcrumb-back",
                isFirst: true,
                isLast: true
            })}
        </View>;
    }

    return <>
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            role="list"
        >
            {renderEntries(false)}
        </View>
        {
            isMeasureEnabled ?
                <ScrollView
                    onContentSizeChange={onMeasureContentSizeChange}
                    importantForAccessibility="no-hide-descendants"
                    showsHorizontalScrollIndicator={false}
                    style={stylesheet.measure}
                    scrollEnabled={false}
                    aria-hidden={true}
                    horizontal={true}
                >
                    <BreadcrumbContext.Provider
                        value={measureContext}
                    >
                        <View
                            style={[
                                stylesheet.container,
                                containerDynamicStyle
                            ]}
                        >
                            {renderEntries(true)}
                        </View>
                    </BreadcrumbContext.Provider>
                </ScrollView>
            :
                null
        }
    </>;
};
export default BreadcrumbList;
